import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { AuthService } from '@services/auth.service';
import { ApiResponse, apiValue } from '@classes/api-response';
import { Order } from '@models/order';

export interface DiscountPromo {
  _id?: string;
  name: string;
  type: 'PERCENT' | 'FIXED' | string;
  value: number;
  active?: boolean;
  eligibleCategories?: string[];
}

export interface TrunkShowEvent {
  _id?: string;
  name?: string;
  vendor?: string;
  vendorName?: string;
  store?: number | string;
  startDate?: string | Date;
  endDate?: string | Date;
  discountPercent?: number;
  notes?: string;
}

export interface AcknowledgmentTemplate {
  _id?: string;
  name?: string;
  body?: string;
  version?: number;
}

@Injectable({
  providedIn: 'root',
})
export class PaymentService {
  private baseUrl = `${environment.base_url}/payment`;
  private reportUrl = `${environment.base_url}/report`;

  constructor(private http: HttpClient, private auth: AuthService) {}

  listDiscounts(): Observable<DiscountPromo[]> {
    return this.http
      .get(`${this.baseUrl}/discounts`)
      .pipe(
        map((res: ApiResponse<any>) => apiValue(res, 'discounts', [])),
        catchError(() => of([]))
      );
  }

  saveDiscount(discount: DiscountPromo): Observable<DiscountPromo> {
    if (discount._id) {
      return this.http
        .put(`${this.baseUrl}/discounts/${discount._id}`, discount)
        .pipe(map((res: ApiResponse<any>) => apiValue(res, 'discount')));
    }
    return this.http
      .post(`${this.baseUrl}/discounts`, discount)
      .pipe(map((res: ApiResponse<any>) => apiValue(res, 'discount')));
  }

  listTrunkShows(): Observable<TrunkShowEvent[]> {
    const store = this.auth.getStore();
    return this.http
      .get(`${this.baseUrl}/trunk-shows/store/${store}`)
      .pipe(
        map((res: ApiResponse<any>) => apiValue(res, 'events', [])),
        catchError(() => of([]))
      );
  }

  saveTrunkShow(event: TrunkShowEvent): Observable<TrunkShowEvent> {
    if (event._id) {
      return this.http
        .put(`${this.baseUrl}/trunk-shows/${event._id}`, event)
        .pipe(map((res: ApiResponse<any>) => apiValue(res, 'event')));
    }
    return this.http
      .post(`${this.baseUrl}/trunk-shows`, event)
      .pipe(map((res: ApiResponse<any>) => apiValue(res, 'event')));
  }

  getAckTemplate(): Observable<AcknowledgmentTemplate> {
    return this.http
      .get(`${this.baseUrl}/acknowledgment-template`)
      .pipe(
        map((res: ApiResponse<any>) => apiValue(res, 'template', { name: '', body: '' })),
        catchError(() => of({ name: '', body: '' }))
      );
  }

  saveAckTemplate(template: AcknowledgmentTemplate): Observable<AcknowledgmentTemplate> {
    return this.http
      .put(`${this.baseUrl}/acknowledgment-template`, template)
      .pipe(map((res: ApiResponse<any>) => apiValue(res, 'template')));
  }

  preview(order: any): Observable<any> {
    return this.http
      .post(`${this.baseUrl}/preview`, { order })
      .pipe(map((res: ApiResponse<any>) => apiValue(res, 'snapshot')));
  }

  charge(payload: any): Observable<Order> {
    return this.http
      .post(`${this.baseUrl}/charge`, payload)
      .pipe(map((res: ApiResponse<any>) => apiValue(res, 'order')));
  }

  allocate(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/allocate`, payload);
  }

  applyStoreCredit(payload: any): Observable<Order> {
    return this.http
      .post(`${this.baseUrl}/credit/apply`, payload)
      .pipe(map((res: ApiResponse<any>) => apiValue(res, 'order')));
  }

  issueStoreCredit(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/credit/issue`, payload);
  }

  transferStoreCredit(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/credit/transfer`, payload);
  }

  applyStructuredCredit(payload: any): Observable<Order> {
    return this.http
      .post(`${this.baseUrl}/credit/structured`, payload)
      .pipe(map((res: ApiResponse<any>) => apiValue(res, 'order')));
  }

  applyTaxAdjustment(payload: any): Observable<Order> {
    return this.http
      .post(`${this.baseUrl}/tax-adjustment`, payload)
      .pipe(map((res: ApiResponse<any>) => apiValue(res, 'order')));
  }

  acknowledge(payload: any): Observable<Order> {
    return this.http
      .post(`${this.baseUrl}/acknowledge`, payload)
      .pipe(map((res: ApiResponse<any>) => apiValue(res, 'order')));
  }

  customerSummary(orderId: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/summary/${orderId}`);
  }

  openInvoices(patientId: string): Observable<Order[]> {
    const store = this.auth.getStore();
    return this.http
      .get(`${this.baseUrl}/open-invoices/store/${store}/patient/${patientId}`)
      .pipe(
        map((res: ApiResponse<any>) => apiValue(res, 'orders', [])),
        catchError(() => of([]))
      );
  }

  availableCredit(patientId: string): Observable<any> {
    return this.http
      .get(`${this.baseUrl}/credit/${patientId}`)
      .pipe(catchError(() => of({ available: 0 })));
  }

  refresh(orderId: string): Observable<Order> {
    return this.http
      .post(`${this.baseUrl}/refresh/${orderId}`, {})
      .pipe(map((res: ApiResponse<any>) => apiValue(res, 'order')));
  }

  dailyLedger(startDate: Date, endDate: Date): Observable<any[]> {
    const store = this.auth.getStore();
    return this.http
      .put(`${this.reportUrl}/ledger/${store}`, { startDate, endDate })
      .pipe(map((res: ApiResponse<any>) => apiValue(res, 'rows', [])));
  }

  trunkShowReport(startDate: Date, endDate: Date): Observable<any[]> {
    const store = this.auth.getStore();
    return this.http
      .put(`${this.reportUrl}/trunk-shows/${store}`, { startDate, endDate })
      .pipe(map((res: ApiResponse<any>) => apiValue(res, 'rows', [])));
  }
}
